import axios from 'axios';
import {useIntl} from 'react-intl';
import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import URL from '../../../config/url';
import DataSetJobsUtils, {IJobs} from './JobsDto';
import {getHeaders} from '../../../store/dataService';
import {toast} from '../../../base-components/StudioToast';

export type DataSetId = string;
export type DataSetJobSubscriber = (data: IJobs) => void;

type DataSetJobsSubscribersList = Record<DataSetId, Array<DataSetJobSubscriber>>;

type DataSetJobsContextType = {
  addDataSetJobsSubscriber: (dataSetId: string, subscriber: DataSetJobSubscriber) => void;
  removeDataSetJobsSubscriber: (
    dataSetId: string,
    subscriber: DataSetJobSubscriber
  ) => void;
};

const DataSetJobsContext = createContext<DataSetJobsContextType | null>(null);

type DataSetJobsProviderProps = {children: React.ReactNode};

const POLLING_INTERVAL = 2_000;

/**
 * This context supports the observable pattern by managing multiple subscribers,
 * creating subscriber lists based on the dataset ID. As long as there is at least
 * one subscriber, it will perform polling for the dataset jobs.
 * To stop polling, simply remove all subscribers from all datasets.
 */
export const DataSetJobsProvider = ({children}: DataSetJobsProviderProps) => {
  const intl = useIntl();
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [subscribersList, setSubscribersList] = useState<DataSetJobsSubscribersList>({});

  const addDataSetJobsSubscriber = useCallback((dataSetId, subscriber) => {
    if (dataSetId) {
      setSubscribersList(prevSubscribersList => ({
        ...prevSubscribersList,
        [dataSetId]: [...(prevSubscribersList[dataSetId] || []), subscriber],
      }));
    }
  }, []);

  const removeDataSetJobsSubscriber = useCallback((dataSetId, subscriberToRemove) => {
    if (dataSetId) {
      setSubscribersList(prevSubscribersList => {
        const updatedSubscribersList = {...prevSubscribersList};

        if (updatedSubscribersList[dataSetId]) {
          updatedSubscribersList[dataSetId] = updatedSubscribersList[dataSetId].filter(
            subscriber => subscriber !== subscriberToRemove
          );

          if (updatedSubscribersList[dataSetId].length === 0) {
            delete updatedSubscribersList[dataSetId];
          }
        }

        return updatedSubscribersList;
      });
    }
  }, []);

  const notifySubscribers = useCallback(
    (dataSetId, jobsData) => {
      const subscribers = subscribersList[dataSetId];

      if (subscribers) {
        subscribers.forEach(subscriber => subscriber(new DataSetJobsUtils(jobsData)));
      }
    },
    [subscribersList]
  );

  const stopToPollingDataSetJobs = useCallback(dataSetId => {
    setSubscribersList(prevSubscribersList => {
      const updatedSubscribersList = {...prevSubscribersList};

      delete updatedSubscribersList[dataSetId];

      return updatedSubscribersList;
    });
  }, []);

  const getDataSetJobsStatus = useCallback(
    async function(datasetId: string) {
      try {
        const {
          data: {body: jobs},
        } = await axios.get(URL.DSS_DATASET_JOBS, {
          params: {datasetId},
          headers: {...(await getHeaders())},
        });

        if (Array.isArray(jobs) && jobs.length) {
          notifySubscribers(datasetId, jobs);
        }
      } catch (error) {
        stopToPollingDataSetJobs(datasetId);
        toast.error(intl.formatMessage({id: 'dataSetJobs.error.polling'}));
      }
    },
    [intl, notifySubscribers, stopToPollingDataSetJobs]
  );

  const poll = useCallback(() => {
    const dataSetIds = Object.keys(subscribersList);

    dataSetIds.forEach(dataSetId => getDataSetJobsStatus(dataSetId));
  }, [getDataSetJobsStatus, subscribersList]);

  const startPolling = useCallback(() => {
    if (intervalIdRef.current === null) {
      intervalIdRef.current = setInterval(() => {
        poll();
      }, POLLING_INTERVAL);
    }
  }, [poll]);

  const stopPolling = useCallback(() => {
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);

      intervalIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    startPolling();

    return () => stopPolling();
  }, [startPolling, stopPolling]);

  return (
    <DataSetJobsContext.Provider
      value={{addDataSetJobsSubscriber, removeDataSetJobsSubscriber}}
    >
      {children}
    </DataSetJobsContext.Provider>
  );
};

export const useDataSetJobs = () => {
  const intl = useIntl();
  const context = useContext(DataSetJobsContext);

  if (!context) {
    throw new Error(
      intl.formatMessage({
        id: 'datasetCreation.datasetJobContext.noProvider',
      })
    );
  }

  return context;
};
